import React from 'react';
import { View, StyleSheet, TouchableOpacity, Platform, Image, Dimensions, Text, ImageStyle } from 'react-native';
import { isXl } from '../utils/responsiveStyles';

const { width: screenWidth } = Dimensions.get('window');

const navItems = [
  {
    id: 'home',
    icon: require('../../assets/icons/home.png'),
    iconSelected: require('../../assets/icons/home-selected.png'),
  },
  {
    id: 'portfolio',
    icon: require('../../assets/icons/portfolio.png'),
    iconSelected: require('../../assets/icons/portfolio-selected.png'),
  },
  {
    id: 'events',
    icon: require('../../assets/icons/events.png'),
    iconSelected: require('../../assets/icons/events-selected.png'),
  },
  {
    id: 'more',
    icon: require('../../assets/icons/analytics.png'),
    iconSelected: require('../../assets/icons/analytics-selected.png'),
  },
];

interface BottomNavigationProps {
  onNavigateToProducts?: () => void;
  onNavigateToHome?: () => void;
  onNavigateToEvents?: () => void;
  onNavigateToAnalytics?: () => void;
  showAnalyticsTab?: boolean;
  activeTab?: string;
  onToggleSidebar?: () => void;
}

const tabletNavItems = [
  { id: 'home', label: 'Home' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'events', label: 'Events' },
];

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  onNavigateToProducts,
  onNavigateToHome,
  onNavigateToEvents,
  onNavigateToAnalytics,
  showAnalyticsTab = false,
  activeTab = 'home',
  onToggleSidebar,
}) => {
  const isTablet = isXl(screenWidth);
  const isDesktop = screenWidth >= 1024;
  const visibleNavItems = showAnalyticsTab
    ? navItems
    : navItems.filter((item) => item.id !== 'more');

  if (isDesktop) {
    // Desktop: No mostrar navbar, solo sidebar
    return null;
  }

  if (isTablet) {
    // Tablet: Top horizontal navigation
    return (
      <View style={styles.tabletContainer}>
        <View style={styles.tabletNavContainer}>
          <TouchableOpacity 
            style={styles.tabletLogoContainer}
            onPress={onToggleSidebar}
            activeOpacity={0.7}
          >
            <View style={styles.tabletMenuIcon}>
              <View style={styles.menuLine} />
              <View style={styles.menuLine} />
              <View style={styles.menuLine} />
            </View>
          </TouchableOpacity>
          <View style={styles.tabletTabsContainer}>
            {tabletNavItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <TouchableOpacity
                  key={item.id}
                  style={[styles.tabletTab, isActive && styles.tabletTabActive]}
                  onPress={() => {
                    if (item.id === 'home' && onNavigateToHome) {
                      onNavigateToHome();
                    } else if (item.id === 'portfolio' && onNavigateToProducts) {
                      onNavigateToProducts();
                    } else if (item.id === 'events' && onNavigateToEvents) {
                      onNavigateToEvents();
                    }
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.tabletTabText, isActive && styles.tabletTabTextActive]}>
                    {item.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={styles.tabletSpacer} />
        </View>
      </View>
    );
  }

  // Mobile: Bottom floating navigation
  return (
    <View style={styles.container}>
      <View style={styles.navContainer}>
        {visibleNavItems.map((item) => {
          const isActive = activeTab === item.id;
          return (
            <TouchableOpacity
              key={item.id}
              style={styles.navItem}
              onPress={() => {
                if (item.id === 'home' && onNavigateToHome) {
                  onNavigateToHome();
                } else if (item.id === 'portfolio' && onNavigateToProducts) {
                  onNavigateToProducts();
                } else if (item.id === 'events' && onNavigateToEvents) {
                  onNavigateToEvents();
                } else if (item.id === 'more' && onNavigateToAnalytics) {
                  onNavigateToAnalytics();
                }
              }}
              activeOpacity={0.7}
            >
              <View style={[styles.iconContainer, isActive && styles.activeIconContainer]}>
                <Image
                  source={isActive ? item.iconSelected : item.icon}
                  style={isActive ? styles.iconActive : styles.icon}
                  resizeMode="contain"
                />
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as any,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
    alignItems: 'center',
    paddingBottom: Platform.OS === 'ios' ? 18 : 8,
    zIndex: 1000,
  },
  navContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 30,
    width: screenWidth * 0.8,
    alignSelf: 'center',
    paddingVertical: 0,
    paddingHorizontal: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 6,
  },
  navItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
  },
  iconContainer: {
    width: 46,
    height: 46,
    borderRadius: 23,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  activeIconContainer: {
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 4,
  },
  icon: {
    width: 23,
    height: 23,
  } as ImageStyle,
  iconActive: {
    width: 23,
    height: 23,
  } as ImageStyle,
  // Tablet styles
  tabletContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
    paddingTop: Platform.OS === 'ios' ? 56 : 12,
    paddingHorizontal: 32,
    paddingBottom: 16,
    zIndex: 1000,
  },
  tabletNavContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    paddingHorizontal: 20,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  tabletLogoContainer: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabletMenuIcon: {
    width: 24,
    height: 18,
    justifyContent: 'space-between',
  },
  menuLine: {
    width: 24,
    height: 2,
    backgroundColor: '#1F2937',
    borderRadius: 1,
  },
  tabletTabsContainer: {
    flexDirection: 'row',
    gap: 4,
  },
  tabletTab: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 10,
  },
  tabletTabActive: {
    backgroundColor: '#E0F2FE',
  },
  tabletTabText: {
    fontSize: 16,
    color: '#9CA3AF',
    fontFamily: 'Montserrat_500Medium',
  },
  tabletTabTextActive: {
    color: '#00506E',
    fontFamily: 'Montserrat_600SemiBold',
  },
  tabletUserIcon: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  userIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  userIconText: {
    fontSize: 20,
    color: '#9CA3AF',
  },
  tabletSpacer: {
    width: 40,
  },
});
